import { FC, useRef, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { SaleItemState } from "@/types/sales";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import { productList, ProductListObjParams, ProductListParams } from "@/apis/product";
import { Flex, notification } from "antd";
import { Product } from "@/types/product";

export interface MatchSaleProductRef {
  getMatchedProducts: () => Product[];
}

interface MatchSaleProductProps {
  items: Array<SaleItemState>;
  supplierApi: string;
  setMatchErr?: (val: boolean) => void;
  errCodes?: Array<string>;
}

const MatchSaleProduct = forwardRef<MatchSaleProductRef, MatchSaleProductProps>(
  ({ items, supplierApi, setMatchErr, errCodes }, ref) => {
    const { t } = useTranslation();
    const tableRef = useRef<TableLayoutRef | null>(null);
    const [loading, setLoading] = useState(false);

    // 存储已匹配的产品
    const [matchedProducts, setMatchedProducts] = useState<Product[]>([]);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      getMatchedProducts: () => matchedProducts,
    }), [matchedProducts]);

    // 未匹配的产品（用于显示提示）
    const unmatchedProducts = useMemo(() => {
      const productCodeMap = new Map(matchedProducts.map(p => [p.code, p]));
      const unmatched = items
        .filter(item => !productCodeMap.has(item.product?.code ?? ""))
        .map(item => item.product);
      return unmatched as Product[];
    }, [items, matchedProducts]);

    useEffect(() => {
      if (items.length > 0 && supplierApi) {
        fetchProductsAndMatch();
      }
    }, [items, supplierApi]);

    const fetchProductsAndMatch = async () => {
      if (!!!supplierApi) {
        return notification.error({ message: t("requirement.supplierApiError"), placement: "top" });
      }

      const codes = items.map(item => item.product?.code).filter(Boolean) as string[];
      if (codes.length === 0) return;

      setLoading(true);
      setMatchedProducts([]); // 清空之前的数据
      try {
        const params: ProductListObjParams = {
          categoryId: undefined,
          params: {
            strs: codes,
          },
          wmsApi: supplierApi
        };
        const { body: apiProducts } = await productList(params);

        // 按照原始 items 顺序构建匹配结果
        const matched: Product[] = [];
        const toAdd: Product[] = [];

        for (const item of items) {
          const product = item.product;
          if (!product) continue;

          const existingProduct = apiProducts.find(p => p.code === product.code);
          if (existingProduct) {
            // 接口中存在该产品，直接存储
            matched.push(existingProduct);
          } else {
            // 接口中不存在，需要新增
            toAdd.push(product);
          }
        }

        // 先存储已匹配的产品
        setMatchedProducts(matched);

        // 如果有需要新增的产品，提示手动添加
        if (toAdd.length > 0) {
          notification.error({
            message: t('requirement.swapEnd'),
            description: (
              <>
                <div style={{ color: "red" }}>
                  {t('requirement.noProducts')}
                </div>
                <div>{t('requirement.plAddProdictOne')}</div>
                <div>{t('requirement.plAddProdictTwo')}</div>
              </>
            )
          });
          setMatchErr?.(true);
        } else {
          setMatchErr?.(false);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        notification.error({
          message: t("requirement.productMatchFailed"),
          description: `${t("requirement.productMatchFailedDesc")}:${supplierApi}`,
          placement: "top"
        });
      } finally {
        setLoading(false);
      }
    };

    const headerSub = (
      <Flex style={{ width: "100%" }}>
        {unmatchedProducts.length > 0 && (
          <span style={{ color: "red" }}>{t("requirement.productsWithoutPrice")}</span>
        )}
      </Flex>
    );

    const columns: ColumnsType<SaleItemState> = useMemo(() => [
      {
        title: t("product.code"),
        dataIndex: ['product', 'code'],
        key: 'productCode',
        width: 120,
        render: (_, item) => {
          const code = item.product?.code ?? "";
          const isMatched = matchedProducts.some(p => p.code === code);
          const isErr = !!errCodes && errCodes.includes(code);
          return (
            <SText
              text={code}
              style={{ width: "100%", color: isMatched && !isErr ? undefined : "red" }}
            />
          );
        },
      },
      {
        title: t("product.name"),
        dataIndex: ['product', 'name'],
        key: 'productName',
        width: 150,
        render: (_, item) => <SText text={item.product?.name ?? ""} style={{ width: "100%" }} />,
      },
      {
        title: t("product.model"),
        dataIndex: ['product', 'model'],
        key: 'productModel',
        width: 120,
        render: (_, item) => <SText text={item.product?.model ?? ""} style={{ width: "100%" }} />,
      },
      {
        title: t("sale.quantity"),
        dataIndex: 'quantity',
        width: 100,
        render: (_, item) => <SText text={item.quantity?.toString() ?? ""} style={{ width: "100%" }} />,
      },
      {
        title: t("product.uom"),
        dataIndex: ['product', 'uom'],
        key: 'uom',
        width: 80,
        render: (_, item) => <SText text={item.product?.uom ?? ""} style={{ width: "100%" }} />,
      },
    ], [t, matchedProducts, errCodes]);

    return (
      <STableLayout
        loading={loading}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={"max-content"}
        headerChildren={null}
        headerSub={headerSub}
        tableLayoutRef={tableRef}
        total={items.length}
        handleChange={() => { }}
        pagination={false}
      />
    );
  }
);

MatchSaleProduct.displayName = 'MatchSaleProduct';

export default MatchSaleProduct;